import { Fragment, useContext, useState } from "react";
import PropTypes from "prop-types";
import { JssProvider, createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { ProfileHoc, useProfileState } from "../../umbilical_hocs";
import { withAuth } from "../../context";
import jssVariables from "../../jss_setup";
import { Button } from "../../components/common/Button";
import Profile from "./Profile";
import { Reservations } from "./Reservations";
import MyProfilePoints from "./MyProfilePoints";
import { useSelector } from "react-redux";
import { Quotations } from "./Quotations/index.jsx";

const asset_s3_url = import.meta.env.VITE_APP_S3_ASSETS_URL || "";

const styles = createUseStyles({
  MyProfile: (props) => ({
    position: "relative",
    background: "#FAFAFA",
    padding: [
      jssVariables.navbar_height,
      jssVariables.spacing.center_padding_mobile_large[1],
    ],
    paddingTop: `calc(${jssVariables.navbar_height} * 2)`,
    minHeight: "100vh",
    height: "max-content",
    alignContent: "baseline",
    ...(props?.customStyles?.MyProfile ?? {}),
  }),
  myProfileContr: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    zIndex: 1,
    position: "relative",
    ...(props?.customStyles?.myProfileContr ?? {}),
  }),
  nav: (props) => ({
    display: "grid",
    gridAutoFlow: "column",
    gridGap: jssVariables.half_gap,
    overflow: "auto",
    ...(props?.customStyles?.nav ?? {}),
  }),
  navItem: (props) => ({
    fontSize: "large",
    textAlign: "center",
    fontWeight: "bold",
    borderRadius: jssVariables.half_gap,
    padding: jssVariables.normal_gap,
    cursor: "pointer",
    color: jssVariables.colors.base_color,
    background: "#F1F1F1",
    ...(props?.customStyles?.navItem ?? {}),
  }),
  activeNavItem: (props) => ({
    background: jssVariables.colors.base_color,
    color: "white",
    ...(props?.customStyles?.activeNavItem ?? {}),
  }),
  graphicsMobileTop: (props) => ({
    position: "absolute",
    top: `calc(${jssVariables.navbar_height} + ${jssVariables.normal_gap})`,
    left: jssVariables.normal_gap,
    width: "20%",
    zIndex: 0,
    ...(props?.customStyles?.graphicsMobileTop ?? {}),
  }),
  graphicsMobileBottom: (props) => ({
    position: "absolute",
    bottom: jssVariables.normal_gap,
    left: jssVariables.normal_gap,
    width: "95%",
    zIndex: 0,
    ...(props?.customStyles?.graphicsMobileBottom ?? {}),
  }),
  graphicsDesktopTop: (props) => ({
    display: "none",
    ...(props?.customStyles?.graphicsDesktopTop ?? {}),
  }),
  graphicsDesktopBottom: (props) => ({
    display: "none",
    ...(props?.customStyles?.graphicsDesktopBottom ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    MyProfile: (props) => ({
      padding: `calc(${jssVariables.navbar_height} * 2) ${jssVariables.spacing.center_padding_small[1]}`,
      paddingBottom: `calc(${jssVariables.navbar_height} * 3)`,
      display: "grid",
      justifyItems: "center",
      ...(props?.customStyles?.MyProfile?.smallscreen ?? {}),
    }),
    myProfileContr: (props) => ({
      width: "max-content",
      ...(props?.customStyles?.myProfileContr?.smallscreen ?? {}),
    }),
    graphicsMobileTop: (props) => ({
      display: "none",
      ...(props?.customStyles?.graphicsMobileTop?.smallscreen ?? {}),
    }),
    graphicsMobileBottom: (props) => ({
      display: "none",
      ...(props?.customStyles?.graphicsMobileBottom?.smallscreen ?? {}),
    }),
    graphicsDesktopTop: (props) => ({
      display: "block",
      position: "absolute",
      top: `calc(${jssVariables.navbar_height} + ${jssVariables.normal_gap})`,
      left: `calc(${jssVariables.normal_gap} * 4)`,
      width: "70%",
      zIndex: 0,
      ...(props?.customStyles?.graphicsDesktopTop?.smallscreen ?? {}),
    }),
    graphicsDesktopBottom: (props) => ({
      display: "block",
      position: "absolute",
      bottom: jssVariables.navbar_height,
      right: `calc(${jssVariables.normal_gap} * 5)`,
      width: "55%",
      zIndex: 0,
      ...(props?.customStyles?.graphicsDesktopBottom?.smallscreen ?? {}),
    }),
  },
});
const MyProfile = ({ getGuest }) => {
  const [activeNav, setActiveNav] = useState("myprofile");

  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({ type, brand, objName: "MyProfile" });
  const classes = styles({ activeNav, customStyles });

  const { profile } = useProfileState();

  const { withPoints, withQuotations } = useSelector((state) => {
    const withPoints = state.WebsiteMetadata?.metadata?.points_scheme ?? false;
    const withQuotations = state.WebsiteMetadata?.metadata?.quotations ?? false;
    return { withPoints, withQuotations };
  });

  return (
    <div className={classes.MyProfile}>
      {["easy"].includes(brand) && (
        <Fragment>
          <img
            src={`${asset_s3_url}/my_profile_graphs_mobile_top.svg`}
            alt="drawings"
            className={classes.graphicsMobileTop}
          />
          <img
            src={`${asset_s3_url}/my_profile_graphs_mobile_bottom.svg`}
            alt="drawings"
            className={classes.graphicsMobileBottom}
          />
          <img
            src={`${asset_s3_url}/my_profile_graphs_desktop_top.svg`}
            alt="drawings"
            className={classes.graphicsDesktopTop}
          />
          <img
            src={`${asset_s3_url}/my_profile_graphs_desktop_bottom.svg`}
            alt="drawings"
            className={classes.graphicsDesktopBottom}
          />
        </Fragment>
      )}
      <div className={classes.myProfileContr}>
        <div className={classes.nav}>
          <JssProvider classNamePrefix="myProfileNavBtn___">
            <Button
              text={t("myProfile")}
              size="large"
              onClick={() => setActiveNav("myprofile")}
              empty={activeNav !== "myprofile"}
              revert={activeNav !== "myprofile"}
              customStyles={{ ...customStyles.navBtnStyles }}
            />
            {withPoints && (
              <Button
                text={t("myPoints")}
                size="large"
                onClick={() => setActiveNav("points")}
                empty={activeNav !== "points"}
                revert={activeNav !== "points"}
                customStyles={{ ...customStyles.navBtnStyles }}
              />
            )}
            <Button
              text={t("myBookings")}
              size="large"
              onClick={() => setActiveNav("reservations")}
              empty={activeNav !== "reservations"}
              revert={activeNav !== "reservations"}
              customStyles={{ ...customStyles.navBtnStyles }}
            />
            {withQuotations && (
              <Button
                text={t("myQuotations")}
                size="large"
                onClick={() => setActiveNav("quotations")}
                empty={activeNav !== "quotations"}
                revert={activeNav !== "quotations"}
                customStyles={{ ...customStyles.navBtnStyles }}
              />
            )}
          </JssProvider>
        </div>
        {activeNav === "myprofile" && (
          <Fragment>
            <Profile profile={profile} getGuest={getGuest} />
            {brand === "travily" && (
              <span>
                To update your profile, please contact Anantara Vacation Club's
                Club Services team directly.
              </span>
            )}
          </Fragment>
        )}
        {activeNav === "reservations" && (
          <Fragment>
            <Reservations />
            {brand === "travily" && (
              <span>
                If you cannot find your booking made before 25th January 2024,
                please contact us to make changes or cancel the booking.
              </span>
            )}
          </Fragment>
        )}
        {activeNav === "points" && <MyProfilePoints />}
        {activeNav === "quotations" && <Quotations />}
      </div>
    </div>
  );
};
MyProfile.propTypes = {
  getGuest: PropTypes.func.isRequired,
};
export default ProfileHoc(MyProfile, withAuth);
